import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, shareReplay } from 'rxjs';
import { environment } from '../../environments/environment';
import { Institution } from '../types/rest-api';

@Injectable({
  providedIn: 'root',
})
export class AttributesApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}/v1/attributes`;

  // GET /v1/attributes/institutions
  getInstitutions() {
    return this.http.get<Institution[]>(`${this.baseUrl}/institutions`);
  }

  /**
   * GET /v1/attributes?types=LANGUAGE – fetch language catalog from API.
   *
   * The OpenAPI parameter name is `types` (FastAPI repeats the key for arrays);
   * `types[]` is silently ignored, which would return *all* attribute types and
   * cause the backend to reject non-LANGUAGE ids on PUT …/languages with
   * "Language ids not found" (several language names such as Deutsch, Englisch,
   * Französisch also exist as SUBJECT attributes). The client-side
   * `type === 'LANGUAGE'` filter is a defensive guard in case the server ever
   * loosens the filter.
   */
  getLanguages() {
    return this.http
      .get<
        { id: string; name: string; type: string }[]
      >(this.baseUrl, { params: new HttpParams().append('types', 'LANGUAGE') })
      .pipe(map(attrs => attrs.filter(a => a.type === 'LANGUAGE')));
  }

  /** Job title / function catalog (PEDAGOGIC, LEADERSHIP, ALTERNATIVE) for filters & forms. Cached for the session. */
  private readonly jobFunctionAttributes$ = (() => {
    let params = new HttpParams();
    for (const t of ['PEDAGOGIC', 'LEADERSHIP', 'ALTERNATIVE'] as const) {
      params = params.append('types', t);
    }
    return this.http
      .get<{ id: string; name: string; type: string }[]>(this.baseUrl, {
        params,
      })
      .pipe(shareReplay(1));
  })();

  getJobFunctionAttributes() {
    return this.jobFunctionAttributes$;
  }
}
